import { AbstractAction, action } from '@packages/table-builder/registry/Action';
import type { ActionResult, ComposeModalConfig } from '@packages/table-builder/registry/Action';
import { ActionRegistry } from '@packages/table-builder/registry/ActionRegistry';
import type { ActionExecutionContext } from '@packages/table-builder/registry/ActionRegistry';
import { formBlock } from '@packages/compose/builders';
import { AllowedCompanyMappingService } from '@wealthlane/api-services/wealthlane-identity-service';
import {
  buildCompanyMappingFormDataFromRow,
  fetchMyCompaniesOptions,
  resolveCompanyMappingFormData,
} from '@packages/domain/shared/customer-success-options/helpers';
import { notify, safeApiCall } from '@packages/shared/utils';
import { refreshTable } from '@packages/table-builder/utils/refreshTable';

const MODAL_SIZE = 'sm';

@action
export class FetchCustomerSuccessSourceCompanyOptionsAction extends AbstractAction {
  readonly id = 'FETCH_CUSTOMER_SUCCESS_SOURCE_COMPANY_OPTIONS';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const excludeCompanyId = ctx.params?.excludeCompanyId as string | undefined;
    return (await fetchMyCompaniesOptions(excludeCompanyId ?? null)) as unknown as ActionResult;
  }
}

@action
export class FetchCustomerSuccessDestinationCompanyOptionsAction extends AbstractAction {
  readonly id = 'FETCH_CUSTOMER_SUCCESS_DESTINATION_COMPANY_OPTIONS';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const excludeCompanyId = ctx.params?.excludeCompanyId as string | undefined;
    return (await fetchMyCompaniesOptions(excludeCompanyId ?? null)) as unknown as ActionResult;
  }
}

@action
export class BuildCompanyMappingFormAction extends AbstractAction {
  readonly id = 'BUILD_COMPANY_MAPPING_FORM';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const meta = ctx.meta as unknown as ComposeModalConfig | undefined;
    const mode = meta?.mode;
    const entityId = ctx.params?.id as string | undefined;

    let formData: Record<string, unknown> | undefined;
    if (mode === 'edit' || mode === 'view') {
      const resolved = resolveCompanyMappingFormData(
        ctx.row as Record<string, unknown>,
        ctx.params as Record<string, unknown>,
      );
      if (!resolved) {
        throw new Error(
          '[BUILD_COMPANY_MAPPING_FORM] Missing source/destination company ids — open Edit from the company mapping list.',
        );
      }
      formData = resolved;
    }

    return {
      block: formBlock('wealthlane://forms/company-mapping', mode, {
        entityId,
        ...(formData != null && { data: formData }),
      }),
    };
  }
}

@action
export class GetCompanyMappingForEditAction extends AbstractAction {
  readonly id = 'GET_COMPANY_MAPPING_FOR_EDIT';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const resolved = resolveCompanyMappingFormData(
      ctx.row as Record<string, unknown> | undefined,
      ctx.params as Record<string, unknown>,
    );
    if (resolved) {
      return resolved as ActionResult;
    }

    throw new Error('[GET_COMPANY_MAPPING_FOR_EDIT] Missing company mapping row data');
  }
}

@action
export class SaveCompanyMappingAction extends AbstractAction {
  readonly id = 'SAVE_COMPANY_MAPPING';

  async execute(ctx: ActionExecutionContext): Promise<ActionResult> {
    const payload = ctx.params.payload as unknown as Record<string, unknown>;
    const mappingId = (ctx.params.id ?? payload.id) as string | undefined;

    if (ctx.mode === 'edit' && mappingId) {
      const result = await safeApiCall(() =>
        AllowedCompanyMappingService.putApiIdentityApiAppAllowedCompanyMapping(mappingId, payload),
      );
      if (result == null) {
        return { success: false } as ActionResult;
      }
      notify.success('Company Mapping Edited.');
      refreshTable('CompanyMapping');
      ctx.refreshTable();
      return { success: true } as ActionResult;
    }

    const result = await safeApiCall(() =>
      AllowedCompanyMappingService.postApiIdentityApiAppAllowedCompanyMapping(payload as never),
    );
    if (result == null) {
      return { success: false } as ActionResult;
    }
    notify.success('Company Mapping Added.');
    refreshTable('CompanyMapping');
    ctx.refreshTable();
    return { success: true } as ActionResult;
  }
}

@action
export class AddCompanyMappingAction extends AbstractAction {
  readonly id = 'ADD_COMPANY_MAPPING';

  execute(ctx: ActionExecutionContext) {
    ActionRegistry.emitIntent({
      type: 'OPEN_COMPOSE_MODAL',
      actionId: 'BUILD_COMPANY_MAPPING_FORM',
      context: {
        ...ctx,
        pk: '',
        params: {},
        meta: {
          title: 'Add Mapping',
          entity: 'CompanyMapping',
          size: MODAL_SIZE,
          semanticId: 'addCompanyMappingModal',
          mode: 'create',
        },
      },
    });
  }
}

@action
export class EditCompanyMappingAction extends AbstractAction {
  readonly id = 'EDIT_COMPANY_MAPPING';

  execute(ctx: ActionExecutionContext) {
    const entityId = ctx.pk ?? (ctx.row?.id as string);
    const row = ctx.row as Record<string, unknown>;
    const formIds = buildCompanyMappingFormDataFromRow(row);
    ActionRegistry.emitIntent({
      type: 'OPEN_COMPOSE_MODAL',
      actionId: 'BUILD_COMPANY_MAPPING_FORM',
      context: {
        ...ctx,
        pk: entityId,
        row,
        params: {
          id: entityId,
          ...(formIds ?? {}),
        },
        meta: {
          title: 'Edit Company Mapping',
          entity: 'CompanyMapping',
          size: MODAL_SIZE,
          semanticId: 'editCompanyMappingModal',
          mode: 'edit',
        },
      },
    });
  }
}
